import React from 'react';
import { getFormatLocale } from '../../lib/useNationalEnvironment';

const RE_PREFIX_RE = /^\s*(re|aw|sv|odp|antw|wg)\s*:/i;
const FWD_PREFIX_RE = /^\s*(fwd?|wg|vs|enc|tr|pd|rv|ytr|doorst)\s*:/i;

/** Prefix subject with "Re:" unless it already carries a reply prefix (Re/Aw/Sv/Odp…). */
export function replySubject(subject: string | null | undefined): string {
  const s = subject || '';
  return RE_PREFIX_RE.test(s) ? s : `Re: ${s}`;
}

/** Prefix subject with "Fwd:" unless it already carries a forward prefix. */
export function forwardSubject(subject: string | null | undefined): string {
  const s = subject || '';
  return FWD_PREFIX_RE.test(s) ? s : `Fwd: ${s}`;
}

export function normalizeSubject(subject: string): string {
  return subject
    .replace(/^(\s*(Re|Fwd|Fw|Aw|Odp|Vá|Vál|SV|ODP|R|F)\s*:\s*)+/gi, '')
    .trim()
    .toLowerCase();
}

export interface DetectedTable {
  rows: string[][];
  delimiter: 'tab' | 'semicolon' | 'comma';
}

/**
 * Detect pasted text that looks like a table: rows separated by newlines,
 * cells separated by tabs (Excel/Sheets), semicolons or commas (CSV).
 * Requires consistent column counts across rows. Tabs need ≥2 rows
 * (strong signal); semicolons/commas need ≥3 rows to reduce false
 * positives from ordinary prose.
 */
export function detectTableLikeText(text: string): DetectedTable | null {
  const lines = text
    .replace(/\r\n/g, '\n')
    .replace(/\r/g, '\n')
    .split('\n')
    .map(l => l.replace(/\s+$/, ''))
    .filter(l => l.length > 0);
  if (lines.length < 2) return null;

  const tryDelim = (delim: string, minRows: number): string[][] | null => {
    if (lines.length < minRows) return null;
    const rows = lines.map(l => l.split(delim).map(c => c.trim()));
    const cols = rows[0].length;
    if (cols < 2 || cols > 30) return null;
    if (!rows.every(r => r.length === cols)) return null;
    return rows;
  };

  const tab = tryDelim('\t', 2);
  if (tab) return { rows: tab, delimiter: 'tab' };
  const semi = tryDelim(';', 3);
  if (semi) return { rows: semi, delimiter: 'semicolon' };
  const comma = tryDelim(',', 3);
  if (comma) return { rows: comma, delimiter: 'comma' };
  return null;
}

const TD_STYLE = 'border:1px solid #999;padding:4px 8px;min-width:40px;';
const TH_STYLE = TD_STYLE + 'background:#e8e2d4;font-weight:bold;';

/** Build a simple bordered HTML table with inline styles (email-safe). First row is styled as a header. */
export function buildTableHtml(rows: string[][]): string {
  let html = '<table style="border-collapse:collapse;margin:8px 0;"><tbody>';
  rows.forEach((row, r) => {
    html += '<tr>';
    row.forEach(cell => {
      html += `<td style="${r === 0 ? TH_STYLE : TD_STYLE}">${cell === '' ? '&nbsp;' : escapeHtml(cell)}</td>`;
    });
    html += '</tr>';
  });
  return html + '</tbody></table><p><br/></p>';
}

export function extractName(addr: string | null): string {
  if (!addr) return '?';
  const match = addr.match(/^"?([^"<]+)"?\s*<?/);
  if (match) return match[1].trim();
  return addr.split('@')[0];
}

export function extractEmail(addr: string | null): string {
  if (!addr) return '';
  const match = addr.match(/<([^>]+)>/);
  if (match) return match[1].trim();
  return addr.trim();
}

export function parseContactAddress(raw: string): { name: string | null; email: string } | null {
  const email = extractEmail(raw);
  if (!email || !email.includes('@')) return null;
  const name = raw.includes('<') ? (extractName(raw) || null) : null;
  return { name, email };
}

/**
 * Split an address list on , ; or newlines, respecting "quoted" names and <angle> sections.
 * A plain split on , breaks "Novák, Karel <k@x.cz>" into a bogus "Novák" recipient.
 */
export function splitAddressList(raw: string): string[] {
  const parts: string[] = [];
  let cur = '';
  let inQuote = false;
  let inAngle = false;
  for (const ch of raw) {
    if (ch === '"' && !inAngle) { inQuote = !inQuote; cur += ch; continue; }
    if (ch === '<' && !inQuote) { inAngle = true; cur += ch; continue; }
    if (ch === '>' && !inQuote) { inAngle = false; cur += ch; continue; }
    if ((ch === ',' || ch === ';' || ch === '\n' || ch === '\r') && !inQuote && !inAngle) {
      parts.push(cur);
      cur = '';
      continue;
    }
    cur += ch;
  }
  parts.push(cur);
  return parts;
}

/** Format a contact for the To/Cc/Bcc field — quotes the name when it contains separators. */
export function formatContactAddress(name: string | null, email: string): string {
  if (!name) return email;
  return /[",;\n]/.test(name) ? `"${name.replace(/"/g, '\\"')}" <${email}>` : `${name} <${email}>`;
}

export function escapeHtml(s: string): string {
  return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;').replace(/'/g, '&#39;');
}

/**
 * Inline CSS from <style> blocks into style attributes, then strip the blocks.
 * Needed when inserting quoted HTML into the contenteditable editor — innerHTML
 * drops <head>/<style>, so emails styled via stylesheet would lose formatting.
 * Handles simple rules (tag/.class/#id/descendant); @-rules and pseudo selectors are skipped.
 * Existing inline styles win over stylesheet rules.
 */
export function inlineHtmlStyles(html: string): string {
  if (typeof window === 'undefined' || !/<style[\s>]/i.test(html)) return html;
  try {
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const styleEls = Array.from(doc.querySelectorAll('style'));
    if (!styleEls.length) return html;
    for (const styleEl of styleEls) {
      const css = (styleEl.textContent || '').replace(/\/\*[\s\S]*?\*\//g, '');
      const ruleRe = /([^{}@]+)\{([^{}]+)\}/g;
      let m: RegExpExecArray | null;
      while ((m = ruleRe.exec(css))) {
        const decl = m[2].trim().replace(/;+$/, '');
        if (!decl) continue;
        for (const rawSel of m[1].split(',')) {
          // strip pseudo-classes/elements — they can't be inlined
          const sel = rawSel.trim().replace(/::?[a-zA-Z-]+(\([^)]*\))?/g, '').trim();
          if (!sel || sel.includes('@')) continue;
          try {
            doc.querySelectorAll(sel).forEach(el => {
              const existing = (el.getAttribute('style') || '').trim();
              el.setAttribute('style', existing ? `${decl};${existing}` : decl);
            });
          } catch { /* invalid selector — skip */ }
        }
      }
      styleEl.remove();
    }
    return doc.body.innerHTML;
  } catch {
    return html;
  }
}

/** Message body as HTML for quoting — keeps bodyHtml (with inlined CSS); plain text is escaped and newlines become <br/>. */
export function bodyToHtml(msg: { bodyHtml: string | null; bodyText: string | null }): string {
  if (msg.bodyHtml) return inlineHtmlStyles(msg.bodyHtml);
  return escapeHtml(msg.bodyText || '').replace(/\n/g, '<br/>');
}

export function avatarColor(name: string): string {
  const colors = ['bg-rust', 'bg-moss', 'bg-wood', 'bg-bark', 'bg-sage', 'bg-ochre'];
  let hash = 0;
  for (let i = 0; i < name.length; i++) hash = name.charCodeAt(i) + ((hash << 5) - hash);
  return colors[Math.abs(hash) % colors.length];
}

export function formatMessageDate(dateStr: string | null): string {
  if (!dateStr) return '';
  const loc = getFormatLocale(); // national environment, not browser locale
  const d = new Date(dateStr);
  const now = new Date();
  const isToday = d.toDateString() === now.toDateString();
  if (isToday) return d.toLocaleTimeString(loc, { hour: '2-digit', minute: '2-digit' });
  const diffDays = Math.floor((now.getTime() - d.getTime()) / 86400000);
  if (diffDays < 7) return d.toLocaleDateString(loc, { weekday: 'short' });
  if (d.getFullYear() !== now.getFullYear()) {
    // 2-digit year for 2000–2099 (21.09.25), full year otherwise (21.09.1997)
    const y = d.getFullYear();
    return d.toLocaleDateString(loc, { day: 'numeric', month: 'numeric', year: y >= 2000 && y <= 2099 ? '2-digit' : 'numeric' });
  }
  return d.toLocaleDateString(loc, { day: 'numeric', month: 'numeric' });
}

/** Full timestamp for the detail view: relative day + time for recent
 * messages (dnes/včera/po 9:30), otherwise compact date + time
 * (14.10.26 9:30) with the same 2-digit-year rule as formatMessageDate. */
export function formatMessageDateTime(dateStr: string | null, t?: (key: string) => string): string {
  if (!dateStr) return '';
  const loc = getFormatLocale();
  const d = new Date(dateStr);
  const now = new Date();
  const time = d.toLocaleTimeString(loc, { hour: '2-digit', minute: '2-digit' });
  const y = d.getFullYear();
  const compact = `${d.toLocaleDateString(loc, { day: 'numeric', month: 'numeric', year: y >= 2000 && y <= 2099 ? '2-digit' : 'numeric' })} ${time}`;
  if (!t) return compact;
  const dayDiff = Math.floor((startOfDay(now).getTime() - startOfDay(d).getTime()) / 86400000);
  if (dayDiff === 0) return `${t('today')} ${time}`;
  if (dayDiff === 1) return `${t('yesterday')} ${time}`;
  if (dayDiff > 1 && dayDiff < 7) return `${d.toLocaleDateString(loc, { weekday: 'short' })} ${time}`;
  return compact;
}

function startOfDay(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate());
}

export const FOLDER_LABELS: Record<string, string> = {
  INBOX: 'Inbox',
  Sent: 'Sent', SENT: 'Sent',
  Drafts: 'Drafts', DRAFTS: 'Drafts',
  Trash: 'Trash', TRASH: 'Trash',
  Junk: 'Spam', SPAM: 'Spam',
  Archive: 'Archive',
  Templates: 'Templates',
};

export const FOLDER_TYPE_MAP: Record<string, 'inbox' | 'sent' | 'drafts' | 'trash' | 'junk' | 'archive' | 'templates'> = {
  'INBOX': 'inbox',
  'Sent': 'sent', 'SENT': 'sent', 'Sent Items': 'sent', 'Odeslaná': 'sent', 'Odeslané': 'sent',
  'INBOX.Sent': 'sent', 'INBOX.Odeslaná': 'sent', 'INBOX.Odeslané': 'sent',
  '[Gmail]/Sent Mail': 'sent',
  'Drafts': 'drafts', 'DRAFTS': 'drafts', 'Draft': 'drafts', 'Koncepty': 'drafts', 'Koncept': 'drafts',
  'INBOX.Drafts': 'drafts', 'INBOX.Koncepty': 'drafts', 'INBOX.Koncept': 'drafts',
  '[Gmail]/Drafts': 'drafts',
  'Trash': 'trash', 'TRASH': 'trash', 'Deleted': 'trash', 'Koš': 'trash', 'Smazané': 'trash',
  'INBOX.Trash': 'trash', 'INBOX.Koš': 'trash', 'INBOX.Smazané': 'trash',
  '[Gmail]/Trash': 'trash',
  'Junk': 'junk', 'SPAM': 'junk', 'Spam': 'junk', 'Junk Email': 'junk', 'Nevyžádané': 'junk',
  'INBOX.Junk': 'junk', 'INBOX.Spam': 'junk', 'INBOX.Nevyžádané': 'junk',
  '[Gmail]/Spam': 'junk',
  'Archive': 'archive', 'Archiv': 'archive', 'All Mail': 'archive', 'ALL': 'archive',
  '[Gmail]/All Mail': 'archive',
  'Templates': 'templates', 'Šablony': 'templates',
};

export function isDraftFolder(name: string): boolean {
  return FOLDER_TYPE_MAP[name] === 'drafts';
}

export const FOLDER_ORDER: Record<string, number> = {
  inbox: 0, drafts: 1, sent: 2, trash: 3, junk: 4, archive: 5, templates: 6,
};

export function folderSortKey(name: string): number {
  const type = FOLDER_TYPE_MAP[name];
  if (type && FOLDER_ORDER[type] !== undefined) return FOLDER_ORDER[type];
  return 100;
}

export function folderLabel(name: string, t: (k: string) => string): string {
  const type = FOLDER_TYPE_MAP[name];
  if (type === 'inbox') return t('emailFolderInbox');
  if (type === 'sent') return t('emailFolderSent');
  if (type === 'drafts') return t('emailFolderDrafts');
  if (type === 'trash') return t('emailFolderTrash');
  if (type === 'junk') return t('emailFolderJunk');
  if (type === 'archive') return t('emailFolderArchive');
  if (type === 'templates') return t('emailFolderTemplates');
  if (FOLDER_LABELS[name]) return FOLDER_LABELS[name];
  return name;
}

export function sanitizeHtml(html: string): string {
  let cleaned = html
    .replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, '')
    .replace(/<style\b[^<]*(?:(?!<\/style>)<[^<]*)*<\/style>/gi, '')
    .replace(/\son\w+\s*=\s*"[^"]*"/gi, '')
    .replace(/\son\w+\s*=\s*'[^']*'/gi, '')
    .replace(/\son\w+\s*=\s*[^\s>]+/gi, '')
    .replace(/javascript:/gi, '')
    .replace(/<iframe\b[^>]*>/gi, '')
    .replace(/<object\b[^>]*>/gi, '')
    .replace(/<embed\b[^>]*>/gi, '')
    .replace(/<meta\b[^>]*>/gi, '')
    .replace(/<link\b[^>]*>/gi, '');
  if (typeof window === 'undefined') return cleaned;
  try {
    const doc = new DOMParser().parseFromString(cleaned, 'text/html');
    doc.querySelectorAll('a').forEach(a => {
      const href = a.getAttribute('href') || '';
      a.setAttribute('target', '_blank');
      a.setAttribute('rel', 'noopener noreferrer nofollow');
      if (href.toLowerCase().startsWith('javascript:')) {
        a.removeAttribute('href');
      } else if (href.startsWith('http://') || href.startsWith('https://')) {
        try {
          const url = new URL(href);
          const text = (a.textContent || '').trim();
          const match = text.match(/^(?:https?:\/\/)?([^\/\s]+)/i);
          if (match) {
            const textHost = match[1].toLowerCase().replace(/^www\./, '');
            const hrefHost = url.hostname.toLowerCase().replace(/^www\./, '');
            if (textHost && textHost !== hrefHost && !textHost.endsWith('.' + hrefHost) && !hrefHost.endsWith('.' + textHost)) {
              a.classList.add('suspicious-link');
              a.setAttribute('title', `⚠ Pozor: odkaz vede na ${url.hostname}, ale zobrazuje ${textHost}`);
            }
          }
        } catch {}
      }
    });
    doc.querySelectorAll('script, style, iframe, object, embed, meta, link').forEach(el => el.remove());
    return doc.body.innerHTML;
  } catch {
    return cleaned;
  }
}

/**
 * Replace remote image sources (http/https <img src|srcset> and inline
 * style url(http…)) with a neutral placeholder so nothing is fetched.
 * Inline images (cid:, data:) are preserved. Returns the rewritten HTML
 * and the number of blocked resources.
 */
export function stripRemoteImages(html: string): { html: string; blocked: number } {
  if (typeof window === 'undefined') return { html, blocked: 0 };
  try {
    const placeholder = 'data:image/svg+xml;utf8,' + encodeURIComponent(
      "<svg xmlns='http://www.w3.org/2000/svg' width='16' height='16'><rect fill='#e8e4dc' width='16' height='16'/></svg>"
    );
    const doc = new DOMParser().parseFromString(html, 'text/html');
    let blocked = 0;
    doc.querySelectorAll('img').forEach(img => {
      const src = (img.getAttribute('src') || '').trim();
      const srcset = (img.getAttribute('srcset') || '').trim();
      if (/^https?:\/\//i.test(src) || /^https?:\/\//i.test(srcset)) {
        img.setAttribute('src', placeholder);
        img.removeAttribute('srcset');
        img.style.backgroundColor = '#e8e4dc';
        blocked++;
      }
    });
    doc.querySelectorAll('[style]').forEach(el => {
      const s = el.getAttribute('style');
      if (s && /url\(\s*['"]?https?:/i.test(s)) {
        el.setAttribute('style', s.replace(/url\(\s*(['"]?)https?:[^)'"]+\1?\s*\)/gi, 'none'));
        blocked++;
      }
    });
    return { html: doc.body.innerHTML, blocked };
  } catch {
    return { html, blocked: 0 };
  }
}

export function linkifyText(text: string): React.ReactNode[] {
  const urlRe = /(https?:\/\/[^\s<>]+)/g;
  const parts = text.split(urlRe);
  return parts.map((part, i) => {
    if (urlRe.test(part)) {
      return (
        <a key={i} href={part} target="_blank" rel="noopener noreferrer" className="text-[#2563eb] underline underline-offset-2 hover:text-[#1d4ed8] hover:bg-[#dbeafe] rounded-sm">
          {part}
        </a>
      );
    }
    return part || null;
  });
}

/**
 * Resize an image data URL to a maximum width, preserving aspect ratio.
 * Uses canvas to downscale. JPEG quality 0.85 for photos, PNG for images
 * with transparency (alpha channel).
 *
 * @param dataUrl  Original image as data URL
 * @param maxWidth Maximum width in pixels (default 1024)
 * @returns Promise resolving to the (possibly resized) data URL
 */
export function resizeImageDataUrl(dataUrl: string, maxWidth = 1024): Promise<string> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      // If image is already within the limit, return as-is
      if (img.width <= maxWidth && img.height <= maxWidth) {
        resolve(dataUrl);
        return;
      }
      // Calculate new dimensions preserving aspect ratio
      const scale = Math.min(maxWidth / img.width, maxWidth / img.height);
      const newWidth = Math.round(img.width * scale);
      const newHeight = Math.round(img.height * scale);
      const canvas = document.createElement('canvas');
      canvas.width = newWidth;
      canvas.height = newHeight;
      const ctx = canvas.getContext('2d');
      if (!ctx) {
        resolve(dataUrl);
        return;
      }
      // Use better image smoothing for downscaling
      ctx.imageSmoothingEnabled = true;
      ctx.imageSmoothingQuality = 'high';
      ctx.drawImage(img, 0, 0, newWidth, newHeight);
      // Detect if the original was a PNG with transparency
      const isPng = dataUrl.startsWith('data:image/png');
      const outputType = isPng ? 'image/png' : 'image/jpeg';
      const quality = isPng ? undefined : 0.85;
      try {
        const resized = canvas.toDataURL(outputType, quality);
        resolve(resized);
      } catch {
        resolve(dataUrl);
      }
    };
    img.onerror = () => reject(new Error('Failed to load image for resize'));
    img.src = dataUrl;
  });
}

export function renderPlainText(text: string) {
  const lines = text.split('\n');
  const elements: React.ReactNode[] = [];
  let listItems: string[] = [];
  let key = 0;

  const bulletRe = /^\s*([*\-•◦○▪►‣⁃])\s+(.+)$/;
  const numberedRe = /^\s*(\d+\.|[a-zA-Z]\))\s+(.+)$/;
  const quoteRe = /^\s*>+\s?(.*)$/;

  const flushList = () => {
    if (listItems.length === 0) return;
    elements.push(
      <ul key={key++} className="list-disc pl-5 my-1 space-y-0.5">
        {listItems.map((item, i) => (
          <li key={i} className="whitespace-pre-wrap break-words">{linkifyText(item)}</li>
        ))}
      </ul>
    );
    listItems = [];
  };

  for (const line of lines) {
    const bulletMatch = line.match(bulletRe);
    const numberedMatch = line.match(numberedRe);
    const quoteMatch = line.match(quoteRe);
    if (bulletMatch) {
      listItems.push(bulletMatch[2]);
    } else if (numberedMatch) {
      listItems.push(`${numberedMatch[1]} ${numberedMatch[2]}`);
    } else if (quoteMatch) {
      flushList();
      elements.push(
        <div key={key++} className="border-l-2 border-bark/20 pl-3 my-1 text-wood text-xs whitespace-pre-wrap break-words">
          {linkifyText(quoteMatch[1] || '')}
        </div>
      );
    } else {
      flushList();
      elements.push(
        <span key={key++} className="block whitespace-pre-wrap break-words">{line ? linkifyText(line) : '\u00A0'}</span>
      );
    }
  }
  flushList();
  return elements;
}

// Translate target languages — labels are native names (language-picker convention, not UI text)
export const TRANSLATE_TARGETS: { code: string; label: string }[] = [
  { code: 'cs', label: 'Čeština' }, { code: 'en', label: 'English' }, { code: 'de', label: 'Deutsch' },
  { code: 'sk', label: 'Slovenčina' }, { code: 'uk', label: 'Українська' }, { code: 'ru', label: 'Русский' },
  { code: 'fr', label: 'Français' }, { code: 'es', label: 'Español' }, { code: 'it', label: 'Italiano' },
  { code: 'pl', label: 'Polski' },
];
